/** @jsxImportSource @emotion/react */
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, css, DialogActions, IconButton } from "@mui/material";
import React, { useState, VFC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../common/Button";

interface Props {
  // common
  // publisherInfo?: PublisherInfoResponse;
  // country: CountryPath;

  // article information
  // publishedAt: DateTime;
  title: string;
  credits?: number;
  // summary: string;
  // selectedCategories: string[];
  // thumbnailAsDefault: boolean;
  // companyInfo: string;
  // mediaContact: string;
  // outputBody: OutputData;
  // businessCategories: BusinessCategory[];

  is_subscripted: boolean;
  remaining_quota?: number;
  defaultCountry?: string;
  thumbnailUrl?: string;

  // Dropzone param
  acceptedFiles: readonly File[];

  // functions
  handleSave: () => Promise<void>;
  handleClickSaveOpen: () => void;
  handleClickOpen: () => void;
  handleClickScheduleOpen: () => void;
}

const PaymentArticleStep: VFC<Props> = (props: Props) => {
  const navigate = useNavigate();
  const [t] = useTranslation();

  /**
   * dialog open
   */
  const [open, setOpen] = useState(false);
  /**
   * handle open dialog
   */
  const handleClickOpen = () => {
    setOpen(true);
  };
  /**
   * handle close dialog
   */
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div css={conentWrapper}>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <div className="p-4">
          <div style={{ textAlign: "right" }}>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className="text-base whitespace-pre-line text-black pt-4 pb-4">
            {t("payment.transition.description")}
            {/* <div css={dialogCaution}>
              <AiOutlineWarning size="28px" style={{ marginRight: 8 }} />
              {t("sidemenu.transition.caution")}
            </div> */}
          </div>
          <DialogActions>
            <Button
              text={t("payment.button.go_to_billing")}
              onClick={() => {
                props.handleSave();
                navigate("/credits_management");
              }}
            />
          </DialogActions>
        </div>
      </Dialog>
      <div className="w-100% p-4 md:p-8">
        <div className="w-100% border p-4 md:p-8 rounded-xl flex flex-col md:flex-row md:items-center gap-4">
          <div className="w-full md:w-50% flex items-center">
            <div className="border rounded-lg">
              <img
                src={
                  props.acceptedFiles[0]
                    ? URL.createObjectURL(props.acceptedFiles[0])
                    : props.thumbnailUrl
                }
                alt="thumbnail"
                css={thumbnailStyle}
              />
            </div>
            <div className="ml-4">
              <div css={titleStyle} className="text text-lg font-bold">
                {props.title}
              </div>
            </div>
          </div>
          <div className="w-full md:w-50% p-2 text">
            {props.defaultCountry !== "JPN" && (
              <>
                <div className="flex text-base justify-between border-b py-2 px-4 font-bold">
                  <div>{t("payment.press_post.title")}</div>
                  {props.is_subscripted ? (
                    props.remaining_quota === 0 ? (
                      <div className="text-red-600 text-sm">
                        {t("payment.press_post.plan_limit_reached")}
                      </div>
                    ) : (
                      <div>{t("payment.press_post.credit_unlimit")}</div>
                    )
                  ) : (
                    <div>{t("payment.press_post.credit_normal")}</div>
                  )}
                </div>
                <div className="text-base py-2 px-4 font-bold">
                  {props.is_subscripted ? (
                    <div>
                      <div className="text-right">
                        {t("credits_management.subscribed")}
                      </div>
                      {props.remaining_quota !== undefined && (
                        <>
                          <div className="text-right text-sm text-gray-600 font-normal mt-1">
                            {t("credits_management.remaining_posts_this_month")}
                            : {props.remaining_quota}
                          </div>
                          {props.remaining_quota === 0 && (
                            <div className="text-right text-xs text-gray-500 font-normal mt-1">
                              {t("credits_management.additional_posts_contact")}{" "}
                              <a
                                target="_blank"
                                href="https://wa.me/+6285119772155"
                                rel="noreferrer"
                                className="underline hover:text-gray-700"
                              >
                                +62 85119772155
                              </a>
                              {" or marketing@vritimes.com"}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : props.credits !== undefined && props.credits > 0 ? (
                    <div className="text-right">
                      {props.credits !== undefined && props.credits}{" "}
                      {t("payment.press_post.credit")} →{" "}
                      <span className="text-xl text-primary">
                        {props.credits !== undefined && props.credits - 1}
                      </span>{" "}
                      {t("payment.press_post.credit")}
                    </div>
                  ) : (
                    <div className="text-yellow-600 text-right">
                      {t("payment.press_post.no_credit")}{" "}
                      <button
                        type="button"
                        onClick={() => {
                          handleClickOpen();
                        }}
                        className="underline hover:text-yellow-800 font-bold"
                      >
                        {t("payment.press_post.no_credit.purchase")}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex justify-end items-center mt-4">
          <Button
            text={t("payment.button.save_as_draft")}
            onClick={() => {
              props.handleSave();
              props.handleClickSaveOpen();
            }}
            variant="outlined"
            className="mr-4"
          />
          <Button
            text={t("general.schedule_post")}
            className="mr-4"
            disabled={
              !(
                (props.credits !== undefined && props.credits > 0) ||
                (props.is_subscripted &&
                  (props.remaining_quota === undefined ||
                    props.remaining_quota > 0))
              )
            }
            onClick={() => {
              props.handleSave();
              props.handleClickScheduleOpen();
            }}
          />
          <Button
            text={t("general.post")}
            disabled={
              !(
                (props.credits !== undefined && props.credits > 0) ||
                (props.is_subscripted &&
                  (props.remaining_quota === undefined ||
                    props.remaining_quota > 0))
              )
            }
            onClick={() => {
              props.handleSave();
              props.handleClickOpen();
            }}
          />
        </div>
      </div>
    </div>
  );
};

const conentWrapper = css`
  background: #ffffff;
  display: flex;
  height: calc(100vh - 80px - 4rem);
`;

const thumbnailStyle = css`
  width: 160px;
  height: 120px;
  min-width: 160px;
  object-fit: contain;
`;

const titleStyle = css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
`;

export default PaymentArticleStep;
